// Claimbutton.js
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import ssaltcoin from '../assets/ssaltcoin.png';
import { useCoin } from '../CoinContext';
import { fetchUserByWallet } from './userUtils';

const MAX_SECONDS = 10800; // 3 hours = 10,800 seconds

function Claimbutton() {
  const { walletAddress, setCurrentPoints } = useCoin();
  const [userData, setUserData] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [pointsGain, setPointsGain] = useState(0);

  const fetchUserData = useCallback(async () => {
    if (!walletAddress) {
      setInitialLoading(false);
      setUserData(null);
      setCurrentPoints(0);
      return;
    }
    setInitialLoading(true); // 로딩 시작
    try {
      const data = await fetchUserByWallet();
      if (data) {
        setUserData(data);
        setCurrentPoints(data.current_points);
      } else {
        console.log('No user record found');
        setUserData(null);
        setCurrentPoints(0);
      }
    } catch (e) {
      console.error('Unexpected error:', e);
      setUserData(null);
      setCurrentPoints(0);
    } finally {
      setInitialLoading(false);
    }
  }, [walletAddress, setCurrentPoints]);

  // walletAddress가 변경될 때마다 fetchUserData 실행
  useEffect(() => { 
    fetchUserData(); 
  }, [fetchUserData]);

  // per_sec 반영된 실시간 포인트 계산 포함 useEffect
  useEffect(() => {
    if (!userData) return;
    const last = userData.last_claim ?? userData.last_login;
    const perSec = userData.per_sec ?? 1; // get_user_total_bonus 테이블 값 활용
    console.log(perSec);
    const baseSec = last ? Math.floor((Date.now() - new Date(last).getTime()) / 1000) : 0;
    setSeconds(Math.min(baseSec, MAX_SECONDS));
    setPointsGain(Math.round(Math.min(baseSec, MAX_SECONDS) * perSec * 100) / 100); // 반드시 숫자
    const timer = setInterval(() => {
      const currSec = last ? Math.floor((Date.now() - new Date(last).getTime()) / 1000) : 0;
      setSeconds(Math.min(currSec, MAX_SECONDS));
      setPointsGain(Math.round(Math.min(currSec, MAX_SECONDS) * perSec * 100) / 100); // 반드시 숫자
    }, 1000);
    return () => clearInterval(timer);
  }, [userData, resetTrigger]);

  const handleClaim = async () => {
    if (!userData || !walletAddress) return;
    if (seconds < 300) {
      alert("Claim available after 5 minutes");
      return;
    }
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) {
        alert("Login required");
        setLoading(false);
        return;
      }
      const res = await fetch("https://dkphrwvcuxvkksvkgqmf.supabase.co/functions/v1/claim-coin", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${accessToken}`,
        },
        body: JSON.stringify({})
      });
      const data = await res.json();
      if (res.ok && data.current_points !== undefined) {
        setCurrentPoints(data.current_points);
        setSeconds(0);
        setResetTrigger(x => x + 1);
        await fetchUserData();
      } else {
        alert(data.error || "Claim failed");
      }
    } catch (e) {
      console.error('Claim error:', e);
      alert("Server communication error");
    }
    setLoading(false);
  };

  // 초기 로딩 중
  if (initialLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', color: '#888' }}>
        Loading...
      </div>
    );
  }

  // 로그인 안됨 또는 유저 데이터 없음
  if (!walletAddress || !userData) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', color: '#888' }}>
        Login required
      </div>
    );
  }

  const canClaim = seconds >= 300; 
  const isMaxed = seconds >= MAX_SECONDS;

  return (
    <div onClick={loading ? undefined : handleClaim}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: loading ? 'not-allowed' : (canClaim ? 'pointer' : 'not-allowed'),
        userSelect: 'none', borderRadius: 20, marginBottom: 8, 
        opacity: loading ? 0.6 : (canClaim ? 1 : 0.5)
      }}
      title={
        isMaxed ? "Maximum 3-hour accumulation complete! Claim now!" :
        canClaim ? "Click to claim!" : 
        "Claim available after 5 minutes"
      }>
      <img src={ssaltcoin} alt="coin" width={32} height={32} style={{ marginRight: 2 }} />
      <div style={{
        background: isMaxed ? '#f44336' : (canClaim ? '#1976d2' : '#888'), 
        borderRadius: 20, width: 110, height: 24,
        display: 'flex', alignItems: 'center', position: 'relative', marginRight: 8,
      }}>
        <span style={{
          width: '100%', textAlign: 'center', color: '#fff', fontSize: 20,
          fontWeight: 600, position: 'absolute', left: 0, top: 0, lineHeight: '24px',
        }}>
          {pointsGain.toFixed(2)}
        </span>
      </div>
    </div>
  );
}

export default Claimbutton;
